import moment from 'moment/moment';
import React, { useMemo } from 'react';
import {
  DataGrid,
  GridColDef,
  CellTypes,
  Button,
  Badge,
  BadgeProps,
} from '@fashionnovateam/nova-ui-toolkit';
import { Link } from 'react-router-dom';
import { startCase } from 'lodash';
import {
  GridPaginationModel,
  GridSortModel,
} from '@fashionnovateam/nova-ui-toolkit/dist/molecules/DataGrid/utils';
import { getApplicationStatus } from '../../Lib/Utils/FormUtils/vendor-application';
import ApplicationStatus from '../../Lib/Constants/vendor-application-status';
import { User, Vendor } from '../../Lib/Api/vendor';
import { getPageLink } from '../Admin/UserRow';
import Roles, {
  hasAccessTo,
  Pages,
  RolesType,
  UserRoleDisplay,
} from '../../Lib/Constants/roles';

type ComplianceRow = {
  id: number;
  vendor_name: CellTypes.DataGridCellCompanyParams;
  application_status: CellTypes.DataGridCellBadgeParams;
  email: CellTypes.DataGridCellStringParams;
  role: CellTypes.DataGridCellCustomParams;
  last_logged_in_at: CellTypes.DataGridCellDateTimeParams;
  login_as: CellTypes.DataGridCellCustomParams;
  coi_expiration_date?: CellTypes.DataGridCellDateTimeParams;
};

interface Props {
  rows: User[];
  count: number;
  loading: boolean;
  updateParams: (value: unknown) => void;
  params: { sortBy: string; sortOrder: 'ASC' | 'DESC'; page: number };
  currentUser: User;
}

const getRoleProperties = (
  role: RolesType,
): { color: BadgeProps['color']; children: string; contrast: 'low' } => {
  let color: BadgeProps['color'] = 'neutral';
  let children: string;
  const contrast = 'low' as const;

  switch (role) {
    case Roles.VENDOR:
      color = 'primary';
      children = 'Vendor Admin';
      break;
    case Roles.VENDOR_OPERATIONS:
      color = 'yellow';
      children = 'Vendor Operations';
      break;
    case Roles.VENDOR_STAFF:
      color = 'purple';
      children = 'Vendor Staff';
      break;
    default:
      color = 'neutral';
      children = UserRoleDisplay[role] || role;
  }

  return { color, children, contrast };
};

function ComplianceDataGrid({
  rows,
  count,
  loading,
  updateParams,
  params,
  currentUser,
}: Props) {
  const handlePaginationModelChange = (model: GridPaginationModel) => {
    updateParams({ page: model.page + 1 });
  };

  const handleSortModelChange = (model: GridSortModel) => {
    if (model.length > 0) {
      updateParams({
        sortBy: model[0].field,
        sortOrder: model[0].sort?.toUpperCase() || 'DESC',
      });
    } else {
      updateParams({
        sortBy: undefined,
        sortOrder: undefined,
      });
    }
  };

  const vendorName = (vendor: Vendor) => {
    if (vendor.is_individual) {
      if (!vendor.first_name && !vendor.last_name) return '-';
      return `${vendor.first_name} ${vendor.last_name}`;
    }
    return vendor.company_name || vendor.legal_name || '-';
  };

  const applicationStatusMapping: {
    [key: string]: 'yellow' | 'green' | 'red' | undefined;
  } = {
    REJECTED: 'red',
    APPROVED: 'green',
    PENDING_APPROVAL: 'yellow',
    PENDING_ACCOUNTING_APPROVAL: 'yellow',
    APPLICATION_PENDING: 'yellow',
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'vendor_name',
        headerName: 'Vendor Name',
        render: 'company',
        flex: 2,
        sortable: true,
        minWidth: 150,
        disableColumnMenu: true,
      },
      {
        flex: 2,
        field: 'application_status',
        headerName: 'Application Status',
        render: 'badge',
        sortable: false,
        minWidth: 180,
        disableColumnMenu: true,
      },
      {
        field: 'email',
        headerName: 'User Email',
        render: 'email',
        sortable: true,
        flex: 2,
        minWidth: 150,
        disableColumnMenu: true,
      },
      {
        field: 'role',
        headerName: 'Role',
        render: 'custom',
        flex: 1,
        sortable: true,
        minWidth: 150,
        disableColumnMenu: true,
      },
      {
        field: 'last_logged_in_at',
        headerName: 'Last Login',
        render: 'dateTime',
        sortable: true,
        flex: 1,
        minWidth: 150,
        disableColumnMenu: true,
      },
      {
        field: 'coi_expiration_date',
        headerName: 'COI Expiration Date',
        render: 'dateTime',
        sortable: true,
        flex: 1,
        minWidth: 150,
        disableColumnMenu: true,
      },
      {
        field: 'login_as',
        headerName: '',
        render: 'custom',
        display: 'flex',
        align: 'center',
        sortable: false,
        flex: 1,
        minWidth: 100,
        disableColumnMenu: true,
      },
    ],
    [],
  );

  const transformedRows = useMemo(
    () =>
      rows.map((u, id) => {
        const { vendor, email } = u;
        if (!vendor) {
          return undefined;
        }
        const applicationStatus = getApplicationStatus(vendor);
        const page = getPageLink(
          currentUser.role,
          u.role,
          ApplicationStatus.APPROVED === applicationStatus,
        );
        const { color, children, contrast } = getRoleProperties(
          u.role as RolesType,
        );

        return {
          id,
          vendor_name: { name: vendorName(vendor) },
          application_status: {
            contrast: 'low',
            children: startCase(applicationStatus.toLowerCase()),
            color: applicationStatusMapping[applicationStatus] ?? 'neutral',
          },
          email,
          role: (
            <Link to={`/admin-console/permissions?roles=${u.role}`}>
              <Badge color={color} contrast={contrast}>
                {children}
              </Badge>
            </Link>
          ),
          last_logged_in_at: {
            date: moment(u.last_logged_in_at).toISOString(),
            activity: true,
            time: true,
          },
          coi_expiration_date: vendor.coi_expiration_date
            ? {
                date: vendor.coi_expiration_date,
                activity: false,
                time: false,
                timeZone: 'utc',
              }
            : undefined,
          login_as: hasAccessTo(
            `users/:id/${page}` as Pages,
            currentUser.role as RolesType,
          ) ? (
            <Link to={`/users/${u.id}/${page}`}>
              <Button size="sm" color="secondary">
                Login As
              </Button>
            </Link>
          ) : undefined,
        } satisfies ComplianceRow;
      }),
    [rows],
  );

  return (
    <DataGrid
      autosizeOnMount
      stateName="compliance_datagrid"
      hideFilterButton
      hideGroupButton
      columns={columns}
      loading={loading}
      rows={transformedRows}
      initialState={{
        pinnedColumns: {
          left: ['vendor_name'],
        },
      }}
      sortingMode="server"
      onSortModelChange={handleSortModelChange}
      paginationMode="server"
      rowCount={count || 0}
      sortModel={
        params?.sortBy
          ? [
              {
                field: params.sortBy,
                sort: params.sortOrder === 'ASC' ? 'asc' : 'desc',
              },
            ]
          : []
      }
      paginationModel={{
        page: params.page - 1,
        pageSize: 15,
      }}
      onPaginationModelChange={handlePaginationModelChange}
      pageSizeOptions={[15]}
    />
  );
}

export default ComplianceDataGrid;
